/**
 * Outgoing microphone routing.
 *
 * Each participant captures the microphone once and publishes clones of it as separate tracks:
 *   - "public"   → main room (everyone hears it)
 *   - "team"     → the player's team room (only teammates are ever connected to it)
 *   - "announce" → main room, quizmaster only
 *
 * At most one route is live at a time. Switching routes is synchronous: the old route is closed
 * before the new one opens, in the same tick as the keypress, so there is no instant where a
 * whisper can reach the public room.
 */

export type RouteName = 'public' | 'team' | 'announce';
export type LiveRoute = RouteName | 'none';

export interface RouteInputs {
  isHost: boolean;
  micMuted: boolean;
  /** Whisper held or toggled. */
  whisper: boolean;
  /** Announcement held or toggled (host only). */
  announce: boolean;
  /** Player is connected to a team room with a published team track. */
  teamReady: boolean;
}

export function liveRoute(s: RouteInputs): LiveRoute {
  if (s.micMuted) return 'none';
  if (s.isHost) return s.announce ? 'announce' : 'public';
  if (s.whisper) {
    // Whispering but the team room isn't connected yet: stay silent rather than fall back to public.
    return s.teamReady ? 'team' : 'none';
  }
  return 'public';
}

/**
 * A hard gate on a MediaStreamTrack's `enabled` flag.
 *
 * LiveKit (and the browser) also write `enabled` — when muting/unmuting, swapping devices,
 * reconnecting. The gate intercepts those writes on this track instance: any attempt to enable
 * the track while the gate is closed is held back until the gate opens. A disabled track sends
 * silence, so a closed gate means no audio leaves on that route, whatever else happens.
 */
export class AudioGate {
  private isOpen = false;
  /** What other code last asked `enabled` to be. */
  private requested: boolean;
  private rawSet: (v: boolean) => void;

  constructor(public readonly track: MediaStreamTrack) {
    const desc = findAccessor(track, 'enabled');
    if (!desc?.get || !desc.set) throw new Error('MediaStreamTrack.enabled is not an accessor');
    const { get, set } = desc;
    this.requested = Boolean(get.call(track));
    this.rawSet = (v) => set.call(track, v);
    Object.defineProperty(track, 'enabled', {
      configurable: true,
      enumerable: true,
      get: () => Boolean(get.call(track)),
      set: (v: boolean) => {
        this.requested = Boolean(v);
        this.rawSet(this.requested && this.isOpen);
      },
    });
    this.rawSet(false);
  }

  get open() {
    return this.isOpen;
  }

  setOpen(open: boolean) {
    this.isOpen = open;
    this.rawSet(open && this.requested);
  }
}

function findAccessor(obj: object, key: string): PropertyDescriptor | undefined {
  for (let o: object | null = obj; o; o = Object.getPrototypeOf(o)) {
    const d = Object.getOwnPropertyDescriptor(o, key);
    if (d) return d;
  }
  return undefined;
}

/** Opens exactly one gate according to the current inputs. */
export class MicRouter {
  private gates = new Map<RouteName, AudioGate>();
  private inputs: RouteInputs = { isHost: false, micMuted: true, whisper: false, announce: false, teamReady: false };
  private current: LiveRoute = 'none';

  /** Called whenever the live route changes (for UI / telemetry). */
  onChange?: (route: LiveRoute) => void;

  get route(): LiveRoute {
    return this.current;
  }

  setGate(route: RouteName, gate: AudioGate | null) {
    const old = this.gates.get(route);
    if (old && old !== gate) old.setOpen(false);
    if (gate) this.gates.set(route, gate);
    else this.gates.delete(route);
    if (route === 'team') this.inputs = { ...this.inputs, teamReady: Boolean(gate) };
    this.apply();
  }

  update(patch: Partial<RouteInputs>) {
    this.inputs = { ...this.inputs, ...patch };
    this.apply();
  }

  private apply() {
    const live = liveRoute(this.inputs);
    // Close everything that should be closed first…
    for (const [name, gate] of this.gates) if (name !== live) gate.setOpen(false);
    // …then open the one live route.
    if (live !== 'none') this.gates.get(live)?.setOpen(true);
    if (live !== this.current) {
      this.current = live;
      this.onChange?.(live);
    }
  }
}
